import { useI18n } from 'vue-i18n'

// Load all flag images eagerly and map locale code to image URL
const flagMap = Object.entries(import.meta.glob('@/assets/images/flags/*', { eager: true })).reduce<
  Record<string, string>
>((acc, [path, mod]) => {
  // Extract filename without extension as locale code, e.g. 'en-US'
  const code =
    path
      .split('/')
      .pop()
      ?.replace(/\.[^/.]+$/, '') || ''
  acc[code] = (mod as { default: string }).default
  return acc
}, {})

export function getLanguageLabel(localeCode: string): string {
  // Format string to Sentence case (First letter uppercase, rest lowercase)
  const toSentence = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()

  try {
    // Use Intl.DisplayNames to get language name from locale code (e.g., 'en' -> 'English')
    const lang =
      new Intl.DisplayNames([localeCode], { type: 'language' }).of(localeCode.split('-')[0]) ||
      localeCode

    return toSentence(lang)
  } catch {
    // Fallback: return the locale code itself in sentence case
    return toSentence(localeCode)
  }
}

// Hook returning array of languages with code, localized label, and flag image
export function useLanguages() {
  const { availableLocales } = useI18n()

  return availableLocales.map((code) => ({
    code,
    label: getLanguageLabel(code), // Localized language name in sentence case
    flag: flagMap[code] ?? '', // Corresponding flag image URL or empty string
  }))
}
